import { useEffect, useState } from "react";
import { CheckCircle2, Lock, RotateCcw, Users2, CalendarClock, BellRing } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errorText } from "@/lib/api";

export const useProjectAccess = (projectId, refreshKey = 0) => {
  const [access, setAccess] = useState(null);
  const load = () => api.get(`/projects/${projectId}/access`).then((r) => setAccess(r.data)).catch(() => {});
  useEffect(() => { load(); }, [projectId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return [access, load];
};

const dueOf = (dl, dept) => (dl?.per || {})[dept] || dl?.default || null;
const daysLeft = (iso) => (iso ? Math.ceil((new Date(iso) - new Date(new Date().toDateString())) / 86400000) : null);
const DueBadge = ({ iso, done }) => {
  const d = daysLeft(iso);
  if (!iso || done) return null;
  const cls = d < 0 ? "text-red-700" : d <= 3 ? "text-amber-700" : "text-slate-500";
  return <span className={`text-[10px] ${cls}`} data-testid="deadline-badge"> · due {iso}{d < 0 ? ` (${-d} d overdue)` : d <= 7 ? ` (${d} d)` : ""}</span>;
};

const DeadlineEditor = ({ projectId, access, onChanged, open, setOpen }) => {
  const [dl, setDl] = useState({ default: access.deadlines?.default || "", per: { ...(access.deadlines?.per || {}) } });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/projects/${projectId}`, { dept_deadlines: { default: dl.default || null, per: Object.fromEntries(Object.entries(dl.per).filter(([, v]) => v)) } }); toast.success("Deadlines saved — departments notified"); setOpen(false); onChanged?.(); }
    catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const remind = async () => {
    try { const { data } = await api.post(`/projects/${projectId}/deadlines/run-now`); toast.success(`Reminders sent: ${data.sent} (H-7 / H-3 / H-1 / overdue rules)`); } catch (e) { toast.error(errorText(e)); }
  };
  if (!open) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)} className="h-7 text-[11px]" data-testid="deadline-toggle"><CalendarClock size={12} className="mr-1" /> {access.deadlines?.default ? `Deadline ${access.deadlines.default}` : "Set deadline"}</Button>
        {access.deadlines?.default && <Button type="button" size="sm" variant="ghost" onClick={remind} className="h-7 text-[11px] text-amber-800" data-testid="deadline-remind-now"><BellRing size={12} className="mr-1" /> Send reminders now</Button>}
      </div>
    );
  }
  return (
    <div className="w-full">
      {open && (
        <div className="w-full mt-2 rounded-md border border-slate-200 bg-slate-50 p-3 grid sm:grid-cols-2 lg:grid-cols-5 gap-2" data-testid="deadline-editor">
          <label className="text-[11px] text-slate-700 lg:col-span-5 flex items-center gap-2">Default deadline (all departments)<input type="date" value={dl.default} onChange={(e) => setDl({ ...dl, default: e.target.value })} className="h-8 rounded border border-slate-300 px-2 text-xs bg-white" data-testid="deadline-default" /><span className="text-[10px] text-slate-500">Reminders go to the bell at H-7, H-3, H-1 and daily when overdue (09:00 WITA).</span></label>
          {access.departments.map((d) => <label key={d} className="text-[10px] text-slate-600 flex flex-col gap-0.5">{d}<input type="date" value={dl.per[d] || ""} onChange={(e) => setDl({ ...dl, per: { ...dl.per, [d]: e.target.value } })} className="h-7 rounded border border-slate-300 px-1.5 text-[11px] bg-white" data-testid={`deadline-${d.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} /></label>)}
          <div className="lg:col-span-5 flex gap-2 justify-end"><Button type="button" size="sm" variant="outline" onClick={() => setOpen(false)} className="h-7 text-[11px]">Cancel</Button><Button type="button" size="sm" onClick={save} disabled={busy} className="h-7 text-[11px] bg-slate-900 hover:bg-emerald-700 text-white" data-testid="deadline-save">{busy ? "Saving…" : "Save deadlines"}</Button></div>
        </div>
      )}
    </div>
  );
};

export const DepartmentBar = ({ access, onChanged, projectId, canEdit }) => {
  const [busy, setBusy] = useState(false);
  const [dlOpen, setDlOpen] = useState(false);
  if (!access) return null;
  const completed = access.completed || {};
  const act = async (dept, verb) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/departments/${encodeURIComponent(dept)}/${verb}`);
      toast.success(verb === "complete" ? `${dept} marked complete — General Manager notified (${data.notified})` : `${dept} reopened — department notified (${data.notified})`);
      onChanged?.();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  if (access.role === "department") {
    const st = completed[access.department];
    const due = dueOf(access.deadlines, access.department);
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="department-scope-bar">
        <Lock size={14} className="shrink-0" />
        <span className="flex-1 min-w-[240px]">You are working as <b>{access.department}</b>: editable sheets {(access.editable_sheets || []).length ? <b>{access.editable_sheets.join(", ")}</b> : <i>none detected for this file</i>}{access.payroll_all ? <> plus <b>every payroll line</b> (HR view)</> : null}. Other sheets are read-only.{due && <span data-testid="department-deadline"> Deadline <b>{due}</b><DueBadge iso={due} done={st?.status === "complete"} /></span>}</span>
        {st?.status === "complete" ? <span className="inline-flex items-center gap-1 text-emerald-800 font-medium" data-testid="department-complete-badge"><CheckCircle2 size={14} /> Marked complete {st.at?.slice(0, 10)}</span>
          : canEdit && <Button size="sm" onClick={() => act(access.department, "complete")} disabled={busy} className="h-8 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="department-complete-button"><CheckCircle2 size={13} className="mr-1" /> Mark {access.department} complete → notify GM</Button>}
      </div>
    );
  }
  if (!["general_manager", "super_admin"].includes(access.role)) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="gm-department-status">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1.5"><Users2 size={12} /> Department budget status</p>
        {canEdit && !dlOpen && <DeadlineEditor key={JSON.stringify(access.deadlines || {})} projectId={projectId} access={access} onChanged={onChanged} open={false} setOpen={setDlOpen} />}
      </div>
      {canEdit && dlOpen && <DeadlineEditor key={"open" + JSON.stringify(access.deadlines || {})} projectId={projectId} access={access} onChanged={onChanged} open setOpen={setDlOpen} />}
      <div className="flex flex-wrap gap-2 mt-2">
        {(access.departments || []).map((d) => {
          const st = completed[d];
          const done = st?.status === "complete";
          const due = dueOf(access.deadlines, d);
          return (
            <span key={d} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${done ? "border-emerald-300 bg-emerald-50 text-emerald-800" : st?.status === "reopened" ? "border-amber-300 bg-amber-50 text-amber-800" : "border-slate-200 text-slate-600"}`} data-testid={`dept-status-${d.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              {done ? <CheckCircle2 size={12} /> : null}{d}{done ? " · complete" : st?.status === "reopened" ? " · reopened" : ""}<DueBadge iso={due} done={done} />
              {done && canEdit && <button type="button" onClick={() => act(d, "reopen")} disabled={busy} title="Return for rework" className="text-amber-700 hover:text-amber-900" data-testid={`dept-reopen-${d.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}><RotateCcw size={11} /></button>}
            </span>
          );
        })}
      </div>
    </div>
  );
};
